import React, { useContext, useMemo } from 'react';
import { sortByPriorityThenAddedDate } from '../helpers/content.helper';
import { SiteContext, SiteContextConfig } from '../models/site-context.model';

export const SiteContentContext = React.createContext<SiteContext & { config: SiteContextConfig }>({
    site: null,
    collections: [],
    components: [],
    sections: [],

    config: { sanitizeHtml: s => s },
});

export const useSiteContext = () => useContext(SiteContentContext);

export const useSite = () => {
    const { site } = useSiteContext();
    return site;
};

export const useCollections = () => {
    const { collections, site } = useSiteContext();

    return useMemo(() => {
        const priority = site?.collectionPriority || [];
        return collections.sort((a, b) => sortByPriorityThenAddedDate(a, b, priority));
    }, [site?.collectionPriority, collections]);
};

export const useComponents = (collectionId: string) => {
    const { components, collections } = useSiteContext();

    return useMemo(() => {
        const priority = collections.find(c => c.id === collectionId)?.componentPriority || [];
        return components
            .filter(v => v.collectionId === collectionId)
            .sort((a, b) => sortByPriorityThenAddedDate(a, b, priority));
    }, [collections, components, collectionId]);
};

export const useSections = (componentId: string) => {
    const { sections, components } = useSiteContext();
    const priority = useMemo(() => {
        return components.find(v => v.id === componentId)?.sectionPriority || [];
    }, [components, componentId]);

    return useMemo(() => {
        return sections
            .filter(s => s.componentId === componentId)
            .sort((a, b) => sortByPriorityThenAddedDate(a, b, priority));
    }, [sections, componentId, priority]);
};
